import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

export default function Checkout() {
  const { cart, subtotal, clearCart, user, setAuthOpen } = useApp();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const shipping = 0;
  const total = subtotal + shipping;

  const [addr, setAddr] = useState({
    full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "",
  });
  const [contact, setContact] = useState(user?.email || user?.phone || "");
  const [attachments, setAttachments] = useState([]);

  const set = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const placeOrder = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!addr.full_name || !addr.phone || !addr.line1 || !addr.city || !addr.state || !addr.pincode) {
      toast.error("Please fill all address fields"); return;
    }
    if (!contact) { toast.error("Provide contact email or phone"); return; }
    if (!cart.length) { toast.error("Cart is empty"); return; }

    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", { items: cart, address: addr, contact, attachments });
      const lines = [
        `*New order from Phone Experts website*`,
        ``,
        `Order ID: ${order.id.slice(0, 8).toUpperCase()}`,
        `Customer: ${addr.full_name}`,
        `Phone: ${addr.phone}`,
        `Contact: ${contact}`,
        ``,
        `*Items:*`,
        ...cart.map((c) => `- ${c.title} · ${c.variant_name} × ${c.qty} = ₹${(c.price_inr * c.qty).toLocaleString("en-IN")}`),
        ``,
        `Subtotal: ₹${subtotal.toLocaleString("en-IN")}`,
        `Shipping: FREE`,
        `*Total: ₹${total.toLocaleString("en-IN")}*`,
        ``,
        `*Delivery address:*`,
        addr.line1,
        addr.line2 || "",
        `${addr.city}, ${addr.state} - ${addr.pincode}`,
        ``,
        `Please confirm availability and share payment details. Thanks!`,
      ].filter(Boolean).join("\n");
      const url = `https://wa.me/917396871520?text=${encodeURIComponent(lines)}`;
      clearCart();
      window.open(url, "_blank", "noopener");
      toast.success("Opening WhatsApp — complete your order there");
      nav(`/order/${order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  if (!cart.length) {
    return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-white/60">Your cart is empty.</div>;
  }

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">Checkout</h1>
      <div className="grid gap-6 lg:grid-cols-[1fr,340px]">
        <div className="border border-white/10 bg-zinc-950 p-6 space-y-6">
          {!user && (
            <div className="border border-yellow-500/30 bg-yellow-500/5 p-4 flex justify-between items-center">
              <p className="text-sm text-yellow-200">Sign in to place your order.</p>
              <Button data-testid="checkout-signin" size="sm" onClick={() => setAuthOpen(true)} className="bg-yellow-500 text-black hover:bg-yellow-400 rounded-none">
                Sign in
              </Button>
            </div>
          )}

          <div>
            <p className="mono-label text-blue-400 mb-3">Contact</p>
            <Label className="text-xs text-white/60">Email or phone for order updates</Label>
            <Input
              data-testid="checkout-contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="you@example.com or +919xxxxxxxxx"
              className="bg-black border-white/10 mt-1"
            />
          </div>

          <div>
            <p className="mono-label text-blue-400 mb-3">Shipping address</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label className="text-xs text-white/60">Full name</Label><Input data-testid="addr-name" value={addr.full_name} onChange={set("full_name")} className="bg-black border-white/10 mt-1"/></div>
              <div><Label className="text-xs text-white/60">Phone</Label><Input data-testid="addr-phone" value={addr.phone} onChange={set("phone")} className="bg-black border-white/10 mt-1"/></div>
              <div className="sm:col-span-2"><Label className="text-xs text-white/60">Address line 1</Label><Input data-testid="addr-line1" value={addr.line1} onChange={set("line1")} className="bg-black border-white/10 mt-1"/></div>
              <div className="sm:col-span-2"><Label className="text-xs text-white/60">Address line 2 (optional)</Label><Input data-testid="addr-line2" value={addr.line2} onChange={set("line2")} className="bg-black border-white/10 mt-1"/></div>
              <div><Label className="text-xs text-white/60">City</Label><Input data-testid="addr-city" value={addr.city} onChange={set("city")} className="bg-black border-white/10 mt-1"/></div>
              <div><Label className="text-xs text-white/60">State</Label><Input data-testid="addr-state" value={addr.state} onChange={set("state")} className="bg-black border-white/10 mt-1"/></div>
              <div><Label className="text-xs text-white/60">Pincode</Label><Input data-testid="addr-pincode" value={addr.pincode} onChange={set("pincode")} className="bg-black border-white/10 mt-1"/></div>
            </div>
          </div>

          <div>
            <p className="mono-label text-blue-400 mb-3">Shipping method</p>
            <div className="border border-emerald-500/60 bg-emerald-600/10 p-4 flex justify-between items-center">
              <div>
                <p className="font-medium">Free shipping</p>
                <p className="text-xs text-white/60 mt-1">All-India delivery · 3–7 business days · Dispatched in 48 hours</p>
              </div>
              <span className="font-mono text-emerald-400">FREE</span>
            </div>
          </div>

          <div>
            <p className="mono-label text-blue-400 mb-3">Photos (optional)</p>
            <p className="text-xs text-white/60 mb-2">Attach a picture of the broken screen, phone label, or IMEI so we can verify the exact model before dispatch.</p>
            <ImageUploader
              endpoint="order-attachment"
              value={attachments}
              onChange={setAttachments}
              max={5}
            />
          </div>

          <div data-testid="whatsapp-info" className="border border-emerald-500/40 bg-emerald-600/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="h-5 w-5 text-emerald-400"/>
              <p className="font-display text-lg">Order confirmation via WhatsApp</p>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Once you place the order, your details open in WhatsApp with our team at
              <span className="font-mono text-emerald-300"> +91 73968 71520</span>. We'll confirm stock and share a payment link within minutes.
            </p>
          </div>
        </div>

        <aside className="border border-white/10 bg-zinc-950 p-5 h-fit">
          <p className="mono-label text-white/50 mb-4">Summary</p>
          <div className="space-y-2 text-sm">
            {cart.map((c, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span className="text-white/70 truncate">{c.title} <span className="text-white/40">× {c.qty}</span></span>
                <span className="font-mono shrink-0">{fmtINR(c.price_inr * c.qty)}</span>
              </div>
            ))}
            <div className="border-t border-white/10 my-2"/>
            <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span className="font-mono">{fmtINR(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Shipping</span><span className="font-mono text-emerald-400">FREE</span></div>
            <div className="flex justify-between text-base pt-1"><span>Total</span><span data-testid="checkout-total" className="font-mono text-xl">{fmtINR(total)}</span></div>
          </div>
          <Button data-testid="place-order-btn" disabled={loading} onClick={placeOrder} className="w-full mt-5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-none">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>}
            <MessageCircle className="h-4 w-4 mr-2"/> Order on WhatsApp
          </Button>
          <p className="text-[10px] text-white/40 mt-3 font-mono text-center">
            Chat directly with Phone Experts
          </p>
        </aside>
      </div>
    </div>
  );
}
